import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, concatMap, filter, finalize, first, map, Observable, tap, withLatestFrom } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { LocationModel } from './location.model';
import { LocationActions } from '@states/location/location.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { SplashService } from '../framework/splash/splash.service';
import { AppState } from '../store/app.state';
import * as GeneralActions from '@states/general/general.actions';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserActions } from '@states/user/user.action-types';
import { CameraSettingsActions } from '@states/camera-settings/camera-settings.action-types';
import { HomeActions } from '@states/home/home.action-types';
import { HomeSelectors } from '@states/home/home.selector-types';
import { ONE_DAY_MSEC } from '../helpers/common.helpers';
import { IssuesActions } from '@states/issues/issues.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';

export interface LocationsResolverResult {
  locations: LocationModel.LocationItem[];
}

@Injectable({
  providedIn: 'root',
})
export class LocationsResolver {
  loading = false;

  constructor(
    private authenticationService: AuthenticationService,
    private splashService: SplashService,
    private store: Store<AppState>
  ) {
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): LocationsResolverResult | Observable<LocationsResolverResult> | Promise<LocationsResolverResult> {
    return this.store.pipe(
      concatMap(() => {
        return combineLatest([
          this.store.pipe(select(UserSelectors.isProfileLoaded)),
          this.store.pipe(select(LocationSelectors.isFirstLocationLoaded)),
          this.store.pipe(select(UserSelectors.selectTwilioPttDetails)),
          this.store.pipe(select(UserSelectors.selectVmPlayback)),
          this.store.pipe(select(HomeSelectors.selectInitialLoaded)),
          this.store.pipe(select(OrganizationSelectors.selectIssuesEnabled)),
        ]);
      }),
      tap(([isProfileLoaded, isFirstLocationLoaded, twilioPtt, vmPlayback, homeInitialLoaded, issuesEnabled]) => {
        if (this.authenticationService.isLoggedIn() && !this.loading && !isFirstLocationLoaded && isProfileLoaded) {
          if (!this.loading && !twilioPtt || Date.now() + 2 * 60 * 1000 > twilioPtt.expiresAt) {
            this.store.dispatch(UserActions.renewTwilioPttToken());
          }
          if (!this.loading && !vmPlayback || Date.now() + ONE_DAY_MSEC > vmPlayback.expiresAt) {
            this.store.dispatch(UserActions.renewVmPlaybackToken());
          }

          this.loading = true;

          this.store.dispatch(LocationActions.getViewTypeFromLocalStorage());
          this.store.dispatch(LocationActions.GetLocations());
          this.store.dispatch(CameraSettingsActions.getCameraSettings());
          this.store.dispatch(GeneralActions.getGeneralFromBackend());
          if (issuesEnabled) {
            this.store.dispatch(IssuesActions.getIssues());
            this.store.dispatch(IssuesActions.getCameraIssuesCount());
            this.store.dispatch(IssuesActions.getCoreIssuesCount());
          }
        }

      }),
      filter(([isProfileLoaded, isFirstLocationLoaded, twilioPtt, vmPlayback, homeInitialLoaded, issuesEnabled]) => isFirstLocationLoaded && homeInitialLoaded),
      concatMap(res => this.store.pipe(select(LocationSelectors.selectAllLocations))),
      map(res => {
        return { locations: res };
      }),
      first(),
      finalize(() => {
        this.loading = false;
        this.splashService.hide();
      }),
    );
  }
}
